import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function VerAdminAsociado() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(true);
  const [asociado, setAsociado] = useState<any>(null);
  const [inmobiliaria, setInmobiliaria] = useState<any>(null);
  const [proyectos, setProyectos] = useState<any[]>([]);
  const [resenas, setResenas] = useState<any[]>([]);
  const [cursos, setCursos] = useState<any[]>([]);
  const [propiedades, setPropiedades] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      // Validar acceso primero
      if (!user || !user.tipo?.includes('admin')) {
        setLoading(false);
        return;
      }

      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const asociadoSnap = await getDoc(doc(db, 'asociado', id));
        if (!asociadoSnap.exists()) {
          setLoading(false);
          return Swal.fire({
            icon: 'error',
            title: 'No encontrado',
            text: 'El asociado no existe.'
          }).then(() => navigate('/admin-asociado'));
        }

        const data = asociadoSnap.data();
        setAsociado({ id: asociadoSnap.id, ...data });

        // ✅ Inmobiliaria usando doc.id
        if (data.inmobiliaria_doc_id) {
          const inmoDocRef = doc(db, 'inmobiliaria', data.inmobiliaria_doc_id);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            setInmobiliaria({ id: inmoSnap.id, ...inmoSnap.data() });
          }
        }

        // ✅ Proyectos usando doc.id de inmobiliaria
        if (data.inmobiliaria_doc_id) {
          const proSnap = await getDocs(
            query(collection(db, 'proyectos'), where('inmobiliaria_doc_id', '==', data.inmobiliaria_doc_id))
          );
          setProyectos(proSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        }

        // ✅ Propiedades de la inmobiliaria
        if (data.inmobiliaria_doc_id) {
          try {
            const propsQuery = query(
              collection(db, 'propiedades'),
              where('inmobiliaria_doc_id', '==', data.inmobiliaria_doc_id),
              where('activo', '==', true)
            );
            const propsSnap = await getDocs(propsQuery);
            setPropiedades(propsSnap.docs.map(p => ({ id: p.id, ...p.data() })));
          } catch (error) {
            console.error('Error cargando propiedades:', error);
          }
        }

        // Reseñas
        const resenaSnap = await getDocs(
          query(collection(db, 'resenas'), where('asociado_id', '==', id), where('estatus', '==', 'aprobada'))
        );
        setResenas(resenaSnap.docs.map(d => d.data()));

        // Talleres
        const cursoSnap = await getDocs(
          query(collection(db, 'inscripciones'), where('usuario_id', '==', id), where('estatus', '==', 'completado'))
        );
        setCursos(cursoSnap.docs.map(d => d.data()));

      } catch (err) {
        console.error('Error al cargar asociado:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al cargar los datos.'
        }).then(() => navigate('/admin-asociado'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, id, navigate]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
        <p className="mt-3 text-muted">Cargando información del asociado...</p>
      </div>
    );
  }

  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>No tienes permiso para acceder a esta sección</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-asociado')}>
            Regresar
          </button>
        </div>
      </div>
    );
  }

  if (!asociado) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          <h4>Asociado no encontrado</h4>
          <p>No se encontró el asociado con ID: {id}</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin-asociado')}>
            Regresar a lista de asociados
          </button>
        </div>
      </div>
    );
  }

  // Calcular promedio de calificación
  const promedioCalificacion = resenas.length > 0
    ? (resenas.reduce((acc, r) => acc + (r.calificacion || 0), 0) / resenas.length).toFixed(1)
    : asociado?.calificacion
      ? (asociado.calificacion / 20).toFixed(1)
      : 'N/A';

  return (
    <>
      <UserNav />

      <div className="container py-5">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Administración de Asociados', path: '/admin-asociado' },
            { label: asociado?.nombre || 'Ver Asociado' }
          ]}
        />

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>
            <i className="fas fa-user-friends me-2 text-primary"></i>
            {asociado.nombre}
          </h2>
        </div>

        {/* Tarjetas de Información Principal */}
        <div className="row">
          {/* Columna 1: Perfil */}
          <div className="col-md-4">
            <div className="card shadow-sm">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0"><i className="fas fa-user me-2"></i>Perfil</h5>
              </div>
              <div className="card-body text-center">
                {asociado.foto ? (
                  <img
                    src={asociado.foto}
                    alt={asociado.nombre}
                    className="img-fluid rounded-circle mb-3"
                    style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    className="bg-secondary rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                    style={{ width: '150px', height: '150px' }}
                  >
                    <i className="fas fa-user fa-4x text-white"></i>
                  </div>
                )}
                <h5>{asociado.nombre}</h5>
                <p className="text-muted">{asociado.especialidad || 'Asociado Inmobiliario'}</p>

                {/* Redes Sociales */}
                {(asociado.redes?.facebook || asociado.redes?.instagram || asociado.redes?.twitter || asociado.redes?.linkedin || asociado.sitio_web) && (
                  <div className="mt-3">
                    <h6>Redes Sociales</h6>
                    <div className="d-flex justify-content-center gap-2">
                      {asociado.redes?.facebook && (
                        <a href={asociado.redes.facebook} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                          <i className="fab fa-facebook"></i>
                        </a>
                      )}
                      {asociado.redes?.instagram && (
                        <a href={asociado.redes.instagram} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-danger">
                          <i className="fab fa-instagram"></i>
                        </a>
                      )}
                      {asociado.redes?.twitter && (
                        <a href={asociado.redes.twitter} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-info">
                          <i className="fab fa-twitter"></i>
                        </a>
                      )}
                      {asociado.redes?.linkedin && (
                        <a href={asociado.redes.linkedin} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                          <i className="fab fa-linkedin"></i>
                        </a>
                      )}
                      {asociado.sitio_web && (
                        <a href={asociado.sitio_web} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary">
                          <i className="fas fa-globe"></i>
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna 2: Información de Contacto */}
          <div className="col-md-4">
            <div className="card shadow-sm">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0"><i className="fas fa-info-circle me-2"></i>Información de Contacto</h5>
              </div>
              <div className="card-body">
                <p><strong><i className="fas fa-envelope me-2 text-primary"></i>Correo:</strong><br/>{asociado.correo || 'No especificado'}</p>
                <p><strong><i className="fas fa-phone me-2 text-success"></i>Teléfono:</strong><br/>{asociado.telefono || 'No especificado'}</p>
                <p><strong><i className="fas fa-building me-2 text-info"></i>Inmobiliaria:</strong><br/>{inmobiliaria?.nombre || 'Sin asignar'}</p>
                <p><strong><i className="fas fa-briefcase me-2 text-warning"></i>Años de experiencia:</strong><br/>{asociado.años_experiencia || 0} años</p>
                {asociado.licencia && (
                  <p><strong><i className="fas fa-id-card me-2 text-secondary"></i>Licencia:</strong><br/>{asociado.licencia}</p>
                )}
                {asociado.biografia && (
                  <div className="mt-3">
                    <strong><i className="fas fa-user-tie me-2 text-secondary"></i>Biografía:</strong>
                    <p className="text-muted mt-2">{asociado.biografia}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna 3: Estadísticas */}
          <div className="col-md-4">
            <div className="card shadow-sm">
              <div className="card-header bg-success text-white">
                <h5 className="mb-0"><i className="fas fa-chart-bar me-2"></i>Estadísticas</h5>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span><i className="fas fa-star text-warning me-2"></i><strong>Calificación:</strong></span>
                  <span className="badge bg-info fs-6">{promedioCalificacion !== 'N/A' ? `${promedioCalificacion}/5` : promedioCalificacion}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span><i className="fas fa-comment-dots text-primary me-2"></i><strong>Reseñas:</strong></span>
                  <span className="badge bg-primary fs-6">{resenas.length}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span><i className="fas fa-graduation-cap text-success me-2"></i><strong>Talleres:</strong></span>
                  <span className="badge bg-success fs-6">{cursos.length}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                  <span><i className="fas fa-award text-danger me-2"></i><strong>Créditos:</strong></span>
                  <span className="badge bg-danger fs-6">{asociado.creditos || 0}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span><i className="fas fa-project-diagram text-warning me-2"></i><strong>Proyectos:</strong></span>
                  <span className="badge bg-warning fs-6">{proyectos.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Inmobiliaria */}
        {inmobiliaria && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0"><i className="fas fa-building me-2"></i>Inmobiliaria</h5>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5>{inmobiliaria.nombre}</h5>
                      <p className="text-muted mb-0">
                        {inmobiliaria.direccion && `${inmobiliaria.direccion} | `}
                        {inmobiliaria.telefono && `Tel: ${inmobiliaria.telefono}`}
                      </p>
                    </div>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => navigate(`/ver-agencia/${inmobiliaria.inmobiliaria_id}`)}
                    >
                      <i className="fas fa-eye me-2"></i>
                      Ver Inmobiliaria
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Proyectos/Desarrollos */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                <h5 className="mb-0"><i className="fas fa-project-diagram me-2"></i>Desarrollos Activos</h5>
                <span className="badge bg-dark">{proyectos.length}</span>
              </div>
              <div className="card-body">
                {proyectos.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Nombre</th>
                          <th>Ubicación</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proyectos.map(p => (
                          <tr key={p.id}>
                            <td><strong>{p.nombre || 'Proyecto sin nombre'}</strong></td>
                            <td>{p.ubicacion || p.direccion || 'No especificado'}</td>
                            <td>
                              <span className={`badge ${p.estatus === 'activo' ? 'bg-success' : 'bg-secondary'}`}>
                                {p.estatus || 'Activo'}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => navigate(`/ver-project/${p.proyecto_id || p.id}`)}
                              >
                                <i className="fas fa-eye me-1"></i>
                                Ver Proyecto
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-inbox fa-3x mb-3"></i>
                    <p>Sin desarrollos asignados</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Propiedades de la Inmobiliaria */}
        {propiedades.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0"><i className="fas fa-home me-2"></i>Propiedades de la Inmobiliaria</h5>
                  <span className="badge bg-dark">{propiedades.length}</span>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-2">
                      <div className="card text-center bg-primary text-white">
                        <div className="card-body py-2">
                          <h5 className="mb-0">{propiedades.length}</h5>
                          <small>Total</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card text-center bg-success text-white">
                        <div className="card-body py-2">
                          <h5 className="mb-0">{propiedades.filter((p: any) => p.estatus_publicacion === 'aprobada').length}</h5>
                          <small>Aprobadas</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card text-center bg-warning text-white">
                        <div className="card-body py-2">
                          <h5 className="mb-0">{propiedades.filter((p: any) => p.estatus_publicacion === 'pendiente').length}</h5>
                          <small>Pendientes</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card text-center bg-info text-white">
                        <div className="card-body py-2">
                          <h5 className="mb-0">{propiedades.filter((p: any) => (p.estado || p.estatus) === 'Disponible').length}</h5>
                          <small>Disponibles</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card text-center bg-danger text-white">
                        <div className="card-body py-2">
                          <h5 className="mb-0">{propiedades.filter((p: any) => (p.estado || p.estatus) === 'Vendida').length}</h5>
                          <small>Vendidas</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="card text-center bg-secondary text-white">
                        <div className="card-body py-2">
                          <h5 className="mb-0">{propiedades.filter((p: any) => (p.estado || p.estatus) === 'Rentada').length}</h5>
                          <small>Rentadas</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Título</th>
                          <th>Ubicación</th>
                          <th>Precio</th>
                          <th>Estado</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {propiedades.slice(0, 10).map((prop: any) => (
                          <tr key={prop.id}>
                            <td><strong>{prop.titulo || prop.nombre || 'Sin título'}</strong></td>
                            <td>{prop.direccion?.zona || prop.direccion?.area || prop.ciudad || 'Sin ubicación'}</td>
                            <td>
                              {prop.precio_actual_usd ? (
                                <span className="badge bg-success">${prop.precio_actual_usd.toLocaleString()} USD</span>
                              ) : prop.precio_actual_mxn ? (
                                <span className="badge bg-info">${prop.precio_actual_mxn.toLocaleString()} MXN</span>
                              ) : (
                                <span className="text-muted">No especificado</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${
                                (prop.estado || prop.estatus) === 'Disponible' ? 'bg-success' :
                                (prop.estado || prop.estatus) === 'Vendida' ? 'bg-danger' :
                                'bg-secondary'
                              }`}>
                                {prop.estado || prop.estatus || 'Sin estado'}
                              </span>
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-info"
                                  onClick={() => navigate(`/view-admin-property/${prop.id}`)}
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <button
                                  className="btn btn-outline-warning"
                                  onClick={() => navigate(`/edit-property/${prop.id}`)}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {propiedades.length > 10 && (
                      <div className="text-center mt-3">
                        <p className="text-muted">
                          Mostrando 10 de {propiedades.length} propiedades.
                          <button className="btn btn-link" onClick={() => navigate(`/admin-property?inmobiliariaId=${inmobiliaria?.id}`)}>
                            Ver todas
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Talleres y Capacitaciones */}
        {cursos.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0"><i className="fas fa-graduation-cap me-2"></i>Talleres Completados</h5>
                  <span className="badge bg-dark">{cursos.length}</span>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Taller</th>
                          <th>Estatus</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cursos.map((c, i) => (
                          <tr key={i}>
                            <td><strong>{c.nombre || 'Curso sin nombre'}</strong></td>
                            <td>
                              <span className="badge bg-success">{c.estatus || 'Completado'}</span>
                            </td>
                            <td>{c.fecha ? new Date(c.fecha).toLocaleDateString('es-MX') : 'No disponible'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reseñas */}
        {resenas.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                  <h5 className="mb-0"><i className="fas fa-star me-2"></i>Reseñas Aprobadas</h5>
                  <span className="badge bg-dark">{resenas.length}</span>
                </div>
                <div className="card-body">
                  <div className="row">
                    {resenas.slice(0, 6).map((r, i) => (
                      <div key={i} className="col-md-4 mb-3">
                        <div className="card h-100">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>{r.cliente_nombre || 'Cliente'}</strong>
                              <div>
                                {[1, 2, 3, 4, 5].map(star => (
                                  <i
                                    key={star}
                                    className={`fas fa-star ${star <= (r.calificacion || 0) ? 'text-warning' : 'text-muted'}`}
                                  ></i>
                                ))}
                              </div>
                            </div>
                            <p className="mb-0 text-muted">"{r.comentario || 'Sin comentario'}"</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Acciones Administrativas */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0"><i className="fas fa-cogs me-2"></i>Acciones Administrativas</h5>
              </div>
              <div className="card-body">
                <div className="btn-group" role="group">
                  <Link
                    to={`/asociado-page/${id}`}
                    className="btn btn-outline-info"
                  >
                    <i className="fas fa-eye me-2"></i>
                    Ver Perfil Público
                  </Link>
                  <button
                    className="btn btn-outline-warning"
                    onClick={() => navigate(`/editar-asociado/${id}`)}
                  >
                    <i className="fas fa-edit me-2"></i>
                    Editar Asociado
                  </button>
                  {inmobiliaria && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => navigate(`/admin-property?inmobiliariaId=${inmobiliaria.id}`)}
                    >
                      <i className="fas fa-home me-2"></i>
                      Gestionar Propiedades ({propiedades.length})
                    </button>
                  )}
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/admin-asociado')}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Volver a Lista
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
